import React, { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { Search, X, RefreshCw, Maximize2, Minimize2, BarChart3, Trophy, Vote, Table2, LayoutGrid } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import CardPerguntaResultado from '../../components/admin/resultados/CardPerguntaResultado';
import GraficoLinhaTemporal from '../../components/admin/resultados/GraficoLinhaTemporal';
import TabelaDadosBrutos from '../../components/admin/resultados/TabelaDadosBrutos';
import { adminResultadosAPI } from '../../utils/adminResultadosAPI';

const API_ORIGIN = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1').replace(/\/api\/v1$/, '');

const BADGE_TIPO = {
  dinamica: { label: 'Dinâmica', cor: 'bg-blue-50 text-blue-700' },
  portal: { label: 'Legada', cor: 'bg-amber-50 text-amber-700' },
  telao: { label: 'Legada', cor: 'bg-amber-50 text-amber-700' }
};

// Tela "Resultados": lista todas as enquetes (dinâmicas + as duas legadas)
// num grid rico (capa quando tiver, contagem de votos em destaque) — abaixo
// de um cabeçalho "hero" com os números gerais (métrica principal primeiro,
// padrão comum em dashboard de resultado — Typeform/SurveyMonkey/etc. sempre
// abrem com um resumo antes do detalhe por pergunta). Clicar numa enquete
// abre os gráficos daquela enquete num overlay por cima do grid (não troca
// de página) — a grade fica viva por baixo, com a posição de rolagem
// preservada; fechar (X, clique fora ou Esc) só dispensa o overlay, sem
// sensação de "ir pra outra tela". Web search confirmou esse é o padrão
// recomendado pra mudanças que não são página nova de verdade: evitar
// empurrar histórico do navegador e usar um painel por cima em vez de trocar
// o conteúdo inteiro (ver discussão com o usuário — botão "Voltar" e botão
// físico do navegador não combinavam antes).
const ResultadosEnquetes = () => {
  const [enquetes, setEnquetes] = useState([]);
  const [loadingLista, setLoadingLista] = useState(true);
  const [busca, setBusca] = useState('');
  const [selecionada, setSelecionada] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loadingResultado, setLoadingResultado] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const [verDadosBrutos, setVerDadosBrutos] = useState(false);
  const [telaCheia, setTelaCheia] = useState(false);

  useEffect(() => {
    adminResultadosAPI.listarParaFiltro()
      .then(setEnquetes)
      .catch((error) => toast.error(error.userMessage || 'Erro ao carregar enquetes'))
      .finally(() => setLoadingLista(false));
  }, []);

  // Esc fecha o overlay igual ao Modal.js genérico do resto do painel —
  // consistência de gesto em todo o admin.
  useEffect(() => {
    if (!selecionada) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelecionada(null);
        setResultado(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selecionada]);

  const abrir = async (item) => {
    setSelecionada(item);
    setLoadingResultado(true);
    setResultado(null);
    setVerDadosBrutos(false);
    try {
      const dados = await adminResultadosAPI.obter(item.tipo, item.id);
      setResultado(dados);
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao carregar resultados');
    } finally {
      setLoadingResultado(false);
    }
  };

  const fechar = () => {
    setSelecionada(null);
    setResultado(null);
    setTelaCheia(false);
  };

  // Recarrega sem tirar o resultado atual da tela nem resetar "ver dados
  // brutos" — só o ícone gira enquanto busca, o conteúdo antigo continua
  // visível até o novo chegar (sem piscar/zerar a tela a cada atualização).
  const atualizar = async () => {
    if (!selecionada) return;
    setAtualizando(true);
    try {
      const dados = await adminResultadosAPI.obter(selecionada.tipo, selecionada.id);
      setResultado(dados);
      toast.success('Resultados atualizados');
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao atualizar resultados');
    } finally {
      setAtualizando(false);
    }
  };

  const buscaLimpa = busca.trim().toLowerCase();
  const enquetesFiltradas = buscaLimpa
    ? enquetes.filter((e) => e.titulo.toLowerCase().includes(buscaLimpa))
    : enquetes;

  const totalGeralVotos = useMemo(() => enquetes.reduce((soma, e) => soma + e.totalVotos, 0), [enquetes]);
  const maisVotada = useMemo(
    () => (enquetes.length ? [...enquetes].sort((a, b) => b.totalVotos - a.totalVotos)[0] : null),
    [enquetes]
  );

  return (
    <AdminLayout title="Resultados">
      <Toaster position="top-center" />

      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1F3473] to-[#40A1E6] text-white p-6 sm:p-8 mb-6">
        <div className="absolute -top-12 -right-12 w-56 h-56 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10">
          <div className="flex items-center gap-2 text-blue-100 text-sm font-medium mb-2">
            <BarChart3 className="w-4 h-4" /> Central de Resultados
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-1">Veja o que a população está dizendo</h1>
          <p className="text-blue-100 text-sm sm:text-base max-w-xl">
            Gráficos detalhados de cada consulta pública, dinâmica ou legada, em um só lugar.
          </p>

          {!loadingLista && enquetes.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6 max-w-2xl">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                <p className="text-2xl font-bold leading-none">{enquetes.length}</p>
                <p className="text-xs text-blue-100 mt-1">enquete(s)</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                <p className="text-2xl font-bold leading-none">{totalGeralVotos.toLocaleString('pt-BR')}</p>
                <p className="text-xs text-blue-100 mt-1">votos no total</p>
              </div>
              {maisVotada && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 col-span-2 sm:col-span-1">
                  <div className="flex items-start gap-1.5">
                    <Trophy className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-bold leading-snug line-clamp-2">{maisVotada.titulo}</p>
                  </div>
                  <p className="text-xs text-blue-100 mt-1">mais votada</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-6 max-w-md">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar enquete..."
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all"
          />
        </div>
      </div>

      {loadingLista ? (
        <div className="flex justify-center py-16"><div className="loading-spinner" /></div>
      ) : enquetesFiltradas.length === 0 ? (
        <p className="text-gray-500 text-center py-16">Nenhuma enquete encontrada.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {enquetesFiltradas.map((item) => {
            const badge = BADGE_TIPO[item.tipo];
            const temImagem = !!item.imagemCapa;
            return (
              <button
                key={`${item.tipo}-${item.id}`}
                onClick={() => abrir(item)}
                className="relative overflow-hidden text-left rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-white"
                style={temImagem ? { backgroundImage: `url(${API_ORIGIN}${item.imagemCapa})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
              >
                {temImagem && <div className="absolute inset-0 bg-white/85" />}
                <div className="relative z-10 p-5">
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mb-3 ${badge.cor}`}>
                    {badge.label}
                  </span>
                  <h3 className="font-bold text-gray-900 text-lg mb-4 leading-snug">{item.titulo}</h3>
                  <div className="flex items-center gap-1.5 text-primary-700">
                    <Vote className="w-4 h-4 flex-shrink-0" />
                    <span className="text-2xl font-extrabold">{item.totalVotos.toLocaleString('pt-BR')}</span>
                    <span className="text-xs text-gray-500 font-medium">voto(s)</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selecionada && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={fechar} />
          <div
            className={`relative w-full bg-corumba-light shadow-2xl animate-modal-in flex flex-col ${
              telaCheia
                ? 'h-screen max-h-screen rounded-none'
                : 'max-w-6xl max-h-[94vh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl overflow-hidden'
            }`}
          >
            {/* Barra fina só com X/tela cheia — o hero colorido logo abaixo já
                carrega título/contexto, essa aqui existe só pra garantir um
                fechar sempre visível mesmo rolado lá embaixo entre várias
                perguntas. bg-white/20 + borda (não só /10 sem borda) de
                propósito: só a cor branca do ícone contra o azul escuro não
                bastava, o botão em si (a "pílula" de fundo) ficava quase
                invisível em repouso — reforçado aqui e no Modal.js/PreviewModal.js. */}
            <div className="sticky top-0 z-10 flex items-center justify-end gap-2 px-4 sm:px-6 py-2 bg-primary-600 flex-shrink-0">
              <button
                onClick={() => setTelaCheia((v) => !v)}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-white bg-white/20 border border-white/30 hover:bg-white/30 transition-colors"
                aria-label={telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
                title={telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
              >
                {telaCheia ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={fechar}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-white bg-white/20 border border-white/30 hover:bg-white/30 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {loadingResultado ? (
                <div className="flex justify-center py-16"><div className="loading-spinner" /></div>
              ) : resultado ? (
                <>
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1F3473] to-[#40A1E6] text-white p-6 sm:p-7 mb-6">
                    <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
                    <div className="relative z-10 flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold">{resultado.titulo}</h2>
                        <p className="text-blue-100 text-sm mt-1.5 flex items-center gap-1.5">
                          <Vote className="w-4 h-4" /> {resultado.totalRespondentes.toLocaleString('pt-BR')} respondente(s)
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={atualizar}
                          disabled={atualizando}
                          title="Atualizar resultados"
                          className="flex items-center gap-1.5 text-sm font-medium bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg px-3.5 py-2 transition-colors disabled:opacity-60"
                        >
                          <RefreshCw className={`w-4 h-4 ${atualizando ? 'animate-spin' : ''}`} />
                          <span className="hidden sm:inline">Atualizar</span>
                        </button>
                        {resultado.perguntas.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setVerDadosBrutos((v) => !v)}
                            className="flex items-center gap-1.5 text-sm font-medium bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg px-3.5 py-2 transition-colors"
                          >
                            {verDadosBrutos ? <LayoutGrid className="w-4 h-4" /> : <Table2 className="w-4 h-4" />}
                            <span className="hidden sm:inline">{verDadosBrutos ? 'Ver gráficos' : 'Ver dados brutos'}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {resultado.serieTemporal?.pontos?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 mb-5">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">Votos ao longo do tempo</h3>
                      <p className="text-xs text-gray-400 mb-3">
                        {resultado.serieTemporal.granularidade === 'mensal' ? 'Agrupado por mês' : 'Agrupado por dia'}
                      </p>
                      <GraficoLinhaTemporal serieTemporal={resultado.serieTemporal} />
                    </div>
                  )}

                  {resultado.perguntas.length === 0 ? (
                    <p className="text-gray-500 text-center py-16">Nenhuma pergunta encontrada nesta enquete.</p>
                  ) : verDadosBrutos ? (
                    <TabelaDadosBrutos perguntas={resultado.perguntas} />
                  ) : (
                    <div className="grid grid-cols-1 gap-5">
                      {resultado.perguntas.map((pergunta) => (
                        <CardPerguntaResultado key={pergunta.id} pergunta={pergunta} />
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>

            <div className="flex-shrink-0 flex justify-center border-t border-gray-100 px-4 sm:px-6 py-3.5 sm:py-4">
              <button onClick={fechar} className="btn-primary px-8">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ResultadosEnquetes;
